'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  AssetCellHoverCard,
  BodyDefault,
  EndpointMethodBadge,
  EntityIcon,
  MetaDefault,
  MetricItem,
  Skeleton,
} from '@terra/ui-design-system';
import { useAssetTableWebpageEndpoints } from '../../../hooks/api/useAssetTableRelations';
import { getEndpointTabInput } from './asset-tab-inputs';
import { useAssetsTableContext } from './AssetsTableContext';
import { rowClickBlockerProps } from '../rowClickBlocker';

const HOVER_CARD_WIDTH = '420px';
const HOVER_CARD_MAX_HEIGHT = '320px';

function RelatedEndpointsList({
  webpageId,
  isOpen,
  onSelect,
}: {
  webpageId: string;
  isOpen: boolean;
  onSelect: () => void;
}) {
  const t = useTranslations('proxy.assets.table');
  const { onOpenTab, onOpenTabContextMenu } = useAssetsTableContext();
  const { data, isLoading, isError } = useAssetTableWebpageEndpoints({
    webpageId,
    enabled: isOpen,
  });

  if (isLoading) {
    return (
      <VStack gap={2} alignItems="stretch">
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={index} h="24px" borderRadius="sm" />
        ))}
      </VStack>
    );
  }

  if (isError || !data) {
    return (
      <MetaDefault color="fg.error">{t('relatedEndpointsError')}</MetaDefault>
    );
  }

  return (
    <VStack
      gap={1}
      alignItems="stretch"
      maxH={HOVER_CARD_MAX_HEIGHT}
      overflowY="auto"
    >
      <MetaDefault color="fg.subtle" px={1.5}>
        {t('relatedEndpointsTitle', { count: data.totalCount })}
      </MetaDefault>
      {data.endpoints.map((endpoint) => {
        const tab = getEndpointTabInput({
          id: endpoint.id,
          method: endpoint.request.method,
          path: endpoint.path || endpoint.url,
        });

        return (
          <HStack
            key={endpoint.id}
            gap={2}
            px={1.5}
            py={1}
            borderRadius="sm"
            cursor="pointer"
            _hover={{ bg: 'bg.muted' }}
            onClick={() => {
              onSelect();
              onOpenTab(tab);
            }}
            onContextMenu={(event) => onOpenTabContextMenu({ event, tab })}
          >
            <EndpointMethodBadge method={endpoint.request.method} />
            <BodyDefault truncate minW={0}>
              {endpoint.path || endpoint.url}
            </BodyDefault>
          </HStack>
        );
      })}
    </VStack>
  );
}

export function WebpageRelatedEndpointsCell({
  webpageId,
  endpointsCount,
}: {
  webpageId: string;
  endpointsCount: number;
}) {
  const t = useTranslations('proxy.assets.table');
  const [isOpen, setIsOpen] = useState(false);
  const metric = (
    <MetricItem
      hideTooltip
      disabled={endpointsCount === 0}
      icon={
        <EntityIcon
          type="endpoint"
          appearance={endpointsCount === 0 ? 'muted' : 'highlighted'}
          density="compact"
        />
      }
      label={t('relatedEndpointsTitle', { count: endpointsCount })}
      metrics={endpointsCount}
    />
  );

  if (endpointsCount === 0) {
    return metric;
  }

  return (
    <AssetCellHoverCard
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      width={HOVER_CARD_WIDTH}
      placement="bottom-start"
      trigger={
        <Box as="span" display="inline-flex" {...rowClickBlockerProps}>
          {metric}
        </Box>
      }
    >
      <RelatedEndpointsList
        webpageId={webpageId}
        isOpen={isOpen}
        onSelect={() => setIsOpen(false)}
      />
    </AssetCellHoverCard>
  );
}
